import { useEffect, useState } from 'react';
import { RiArrowRightLine, RiHomeSmile2Line, RiToolsLine } from 'react-icons/ri';
import { CATALOG_TYPES } from '../contexts/CatalogTypeContext';
import { useApiCall } from '../hooks/useApiCall';
import { getAssetPath } from '../utils/paths';
import '../styles/CatalogTypeSelector.css';

const TYPE_ICONS = {
  lights_living: RiHomeSmile2Line,
  heavy_machine: RiToolsLine
};

const DEFAULT_SELECTOR_SETTINGS = {
  kicker: 'Building Depot Suriname',
  title: 'Select your department',
  logo_variant: 'logo_color',
  background_image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1800&q=80',
  options: {
    lights_living: {
      label: 'Lights & Living',
      description: 'Lighting, furniture, appliances, outdoor and home finishing.'
    },
    heavy_machine: {
      label: 'Machinery & Equipment',
      description: 'Tools, machines, equipment and heavy duty project supplies.'
    }
  }
};

const mergeSelectorSettings = (settings = {}) => ({
  ...DEFAULT_SELECTOR_SETTINGS,
  ...settings,
  options: {
    lights_living: {
      ...DEFAULT_SELECTOR_SETTINGS.options.lights_living,
      ...(settings.options?.lights_living || {})
    },
    heavy_machine: {
      ...DEFAULT_SELECTOR_SETTINGS.options.heavy_machine,
      ...(settings.options?.heavy_machine || {})
    }
  }
});

const preloadBackground = (url) => new Promise((resolve) => {
  if (!url || typeof window === 'undefined') {
    resolve();
    return;
  }

  const image = new Image();
  image.onload = resolve;
  image.onerror = resolve;
  image.src = url;
});

export default function CatalogTypeSelector({ onSelect }) {
  const apiCall = useApiCall();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let active = true;
    const loader = new AbortController();

    const loadSelector = async () => {
      const response = await apiCall('pages/catalog-selector', 'POST', {}, loader);
      const nextSettings = mergeSelectorSettings(
        response?.success && response.data?.settings ? response.data.settings : DEFAULT_SELECTOR_SETTINGS
      );

      await preloadBackground(nextSettings.background_image);
      if (active) setSettings(nextSettings);
    };

    loadSelector();

    return () => {
      active = false;
      loader.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!settings) {
    return <main className="catalog-selector catalog-selector--loading" aria-busy="true" />;
  }

  const logoVariant = settings.logo_variant === 'logo_white' ? 'logo_white' : 'logo_color';
  const logo = getAssetPath(`logos/${logoVariant}.png`);

  return (
    <main
      className="catalog-selector"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.36), rgba(0, 0, 0, 0.62)), url(${settings.background_image})`
      }}
    >
      <div className="catalog-selector-shell">
        <div className="catalog-selector-brand">
          <img className="catalog-selector-logo" src={logo} alt="Building Depot" />
          <div className="catalog-selector-kicker">{settings.kicker}</div>
          <h1>{settings.title}</h1>
        </div>

        <div className="catalog-selector-options">
          {CATALOG_TYPES.map((type) => {
            const Icon = TYPE_ICONS[type.id];
            const option = settings.options?.[type.id] || type;

            return (
              <button
                key={type.id}
                type="button"
                className={`catalog-selector-option catalog-selector-option--${type.id}`}
                onClick={() => onSelect(type.id)}
              >
                <span className="catalog-selector-icon">
                  <Icon />
                </span>
                <span className="catalog-selector-text">
                  <span className="catalog-selector-title">{option.label}</span>
                  <span className="catalog-selector-description">{option.description}</span>
                </span>
                <RiArrowRightLine className="catalog-selector-arrow" />
              </button>
            );
          })}
        </div>
      </div>
    </main>
  );
}
